import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { memberService } from '../../services/api';
import ManagedProfileForm from '../../components/ManagedProfileForm/ManagedProfileForm';
import { FiSearch, FiMail, FiMessageSquare, FiUsers, FiUserPlus } from 'react-icons/fi';
import './Family.css';

const Family = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [sortByFirst, setSortByFirst] = useState(true);
  const [showAddProfile, setShowAddProfile] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await memberService.getAllMembers({ pageNumber: 1, pageSize: 200 });
      if (response.success) {
        const memberData = Array.isArray(response.data)
          ? response.data
          : (response.data.members || response.data.items || []);
        setMembers(memberData);
      } else {
        setError('Failed to load family members');
      }
    } catch (err) {
      console.error('Failed to fetch members:', err);
      setError('Failed to load family members. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      setSearching(true);
      const response = await memberService.searchMembers(searchQuery, 50);
      if (response.success) {
        // Handle both array and object with members property
        const searchData = Array.isArray(response.data)
          ? response.data
          : (response.data.members || response.data.items || []);
        setSearchResults(searchData);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const displayMembers = searchResults !== null ? searchResults : members;
  const safeDisplayMembers = (Array.isArray(displayMembers) ? [...displayMembers] : []).sort((a, b) => {
    const keyA = sortByFirst
      ? `${a.firstName || ''} ${a.lastName || ''}`
      : `${a.lastName || ''} ${a.firstName || ''}`;
    const keyB = sortByFirst
      ? `${b.firstName || ''} ${b.lastName || ''}`
      : `${b.lastName || ''} ${b.firstName || ''}`;
    return keyA.toLowerCase().localeCompare(keyB.toLowerCase());
  });

  return (
    <div className="family-page">
      <div className="container">
        {/* Page Header */}
        <div className="page-header">
          <h1><FiUsers className="page-header-icon" /> Family Directory</h1>
          <p className="page-subtitle">Connect with your family members</p>
        </div>

        {/* Search Bar */}
        <div className="search-section card">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button type="button" className="search-clear" onClick={clearSearch}>
                  ×
                </button>
              )}
            </div>
            <button type="submit" className="btn btn-primary" disabled={searching}>
              {searching ? 'Searching...' : 'Search'}
            </button>
          </form>
          <div className="family-sort-row">
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAddProfile(true)}>
              <FiUserPlus /> Add Family Member
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSortByFirst(!sortByFirst)}>
              Sorted by: {sortByFirst ? 'First Name' : 'Last Name'}
            </button>
          </div>
          {searchResults !== null && (
            <div className="search-results-info">
              Found {safeDisplayMembers.length} member{safeDisplayMembers.length !== 1 ? 's' : ''}
              <button className="btn-link" onClick={clearSearch}>Clear search</button>
            </div>
          )}
        </div>

        {/* Members Grid */}
        {loading ? (
          <div className="loading-state">
            <span className="spinner spinner-lg"></span>
            <p>Loading family members...</p>
          </div>
        ) : error ? (
          <div className="error-state card">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchMembers}>Try Again</button>
          </div>
        ) : safeDisplayMembers.length === 0 ? (
          <div className="empty-state card">
            <div className="empty-state-icon">👨‍👩‍👧‍👦</div>
            <h3 className="empty-state-title">
              {searchResults !== null ? 'No members found' : 'No family members yet'}
            </h3>
            <p>
              {searchResults !== null
                ? 'Try a different search term'
                : 'Invite your family members to join!'}
            </p>
          </div>
        ) : (
          <div className="members-grid">
            {safeDisplayMembers.map(member => (
              <div key={member.memberID} className="member-card card">
                <Link to={`/member/${member.memberID}`} className="member-card-link">
                  <div className="member-card-avatar">
                    {member.profilePhotoURL ? (
                      <img src={member.profilePhotoURL} alt={member.fullName} className="avatar avatar-xl" />
                    ) : (
                      <div className="avatar avatar-xl">
                        {getInitials(member.firstName, member.lastName)}
                      </div>
                    )}
                  </div>
                  <div className="member-card-info">
                    <h3 className="member-name">
                      {member.fullName}
                      {member.isManagedProfile && <span className="managed-badge" title="Managed profile">👤</span>}
                    </h3>
                    {member.currentCity && (
                      <p className="member-location">{member.currentCity}</p>
                    )}
                    {member.bio && (
                      <p className="member-bio">{member.bio}</p>
                    )}
                  </div>
                </Link>
                <div className="member-card-actions">
                  <Link to={`/messages?to=${member.memberID}`} className="btn btn-ghost btn-sm" title="Send Message">
                    <FiMessageSquare />
                  </Link>
                  {member.email && (
                    <a href={`mailto:${member.email}`} className="btn btn-ghost btn-sm" title="Send Email">
                      <FiMail />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Member Count */}
        {!loading && !error && safeDisplayMembers.length > 0 && (
          <div className="members-count">
            {safeDisplayMembers.length} family member{safeDisplayMembers.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {showAddProfile && (
        <ManagedProfileForm
          onClose={() => setShowAddProfile(false)}
          onCreated={() => { }}
        />
      )}

    </div>
  );
};

export default Family;
